/* stageRecommendationMap.js, maps the user's current build stage
   to the most relevant contextual recommendation. Powers the
   "Recommended at your stage" ambient strip on the Workspace
   Dashboard.

   Rules (per user direction, Phase 1.5):
   - Thinking of building → Equable finance pre-approval
   - Pre-construction (drawings/engineering/contracts) → Conveyancer
   - Slab / Frame / Lockup / Fix / Practical Completion → House
     Inspect (stage-appropriate inspection)
   - Post-handover (Living in / Maintenance) → Defects tradies (P1
     follow-up; for now we route to House Inspect post-handover
     inspection, defects bundle ships in the next iteration)
*/
export const STAGE_RECOMMENDATION_MAP = {
  // ── New Home ───────────────────────────────────────────────────
  'Thinking of building': {
    category: 'partner:equable',
    eyebrow: 'At your stage',
    headline: "Map your borrowing power before you fall in love with a builder.",
    body:
      "Construction loans work very differently to standard home loans. Equable, BuildPilot's exclusive construction-finance partner, can pinpoint your buying power, FHB grant eligibility and the right loan structure for the path you're considering.",
    ctaLabel: 'Explore finance options',
  },
  'Signed up to a builder': {
    category: 'professional:conveyancer',
    eyebrow: 'At your stage',
    headline: 'Get a second set of eyes on your build contract.',
    body:
      'A conveyancer or solicitor reviews variations, allowances, exclusions and progress-payment terms. Typical turnaround 3 to 5 business days. Picking up scope mismatches now is dramatically cheaper than after slab pour.',
    ctaLabel: 'Find a contract reviewer',
  },
  'Construction Drawings/Master Plans': {
    category: 'professional:building_designer',
    eyebrow: 'At your stage',
    headline: "Get an independent designer to pressure-test your plans.",
    body:
      "Builder's standard plans are designed to be built fast, not lived in well. An independent building designer (or architect for custom homes) can spot orientation, flow, storage and light issues while you still have the chance to change them.",
    ctaLabel: 'Find a designer',
  },
  'Engineering': {
    category: 'professional:structural_engineer',
    eyebrow: 'At your stage',
    headline: "Independent engineer review before slab pour.",
    body:
      "Slab and footing design is the single most expensive thing to fix after the fact. An independent structural engineer review at this stage flags ground-condition or load-path issues before concrete is poured.",
    ctaLabel: 'Find an engineer',
  },
  'Energy Rating': {
    category: 'professional:energy_assessor',
    eyebrow: 'At your stage',
    headline: 'Lock in your NatHERS energy rating before council submission.',
    body:
      "An accredited energy assessor models your design against NatHERS / BASIX and flags glazing, insulation or orientation tweaks that lift your rating without lifting the build cost.",
    ctaLabel: 'Find an energy assessor',
  },
  'Building Costs': {
    category: 'partner:equable',
    eyebrow: 'At your stage',
    headline: "Lock in your finance before your final building costs land.",
    body:
      "Final building costs lock in your construction loan amount. Equable, BuildPilot's exclusive construction-finance partner, can confirm borrowing power, FHB grants and structure your progress payments to suit.",
    ctaLabel: 'Explore finance options',
  },
  'Selections': {
    category: 'professional:interior_designer',
    eyebrow: 'At your stage',
    headline: 'Bring an interior designer to your selections appointment.',
    body:
      "Tapware, lighting, tiles, paint colours, splashbacks. Every finishes decision compounds. An interior designer at selections stage saves you from $30k of changed-mind moments and pulls the whole scheme together.",
    ctaLabel: 'Find an interior designer',
  },
  'Site Start': {
    category: 'professional:surveyor',
    eyebrow: 'At your stage',
    headline: 'Confirm your site set-out before the bulldozers arrive.',
    body:
      "A licensed surveyor checks set-out, contour and boundary markers before earthworks begin. Catching an out-of-bounds slab now saves a six-figure rework later.",
    ctaLabel: 'Find a surveyor',
  },
  'Slab Pour': {
    category: 'partner:house_inspect',
    eyebrow: 'At your stage',
    headline: 'Slab inspections catch defects before the frame goes up.',
    body:
      'An independent slab inspection by House Inspect Australia flags reinforcement, set-out and crack issues now, before the frame is erected on top, when fixing them costs 10x more.',
    ctaLabel: 'Find an inspector',
  },
  'Slab Down': {
    category: 'partner:house_inspect',
    eyebrow: 'At your stage',
    headline: 'Get a slab-down inspection before frame work begins.',
    body:
      "Now that the slab is down, an independent inspection by House Inspect Australia documents finish, levels and crack patterns. The report becomes your baseline for the rest of the build.",
    ctaLabel: 'Find an inspector',
  },
  'Frame Stage': {
    category: 'partner:house_inspect',
    eyebrow: 'At your stage',
    headline: 'Frame stage is the highest-ROI inspection in the build.',
    body:
      "Studs, bracing, tie-downs, wet-area framing. Everything that gets covered up by linings happens now. House Inspect Australia is BuildPilot's exclusive inspection partner.",
    ctaLabel: 'Find an inspector',
  },
  'Lockup Stage': {
    category: 'partner:house_inspect',
    eyebrow: 'At your stage',
    headline: 'Lock-up inspection catches what frame stage missed.',
    body:
      "Roof, windows, doors, brickwork and waterproofing all happen between frame and lock-up. Catching defects now is far cheaper than after fix-out, and most builders willingly fix at this stage.",
    ctaLabel: 'Find an inspector',
  },
  'Lockup': {
    category: 'partner:house_inspect',
    eyebrow: 'At your stage',
    headline: 'Lock-up inspection catches what frame stage missed.',
    body:
      "Roof, windows, doors, brickwork and waterproofing all happen between frame and lock-up. Catching defects now is far cheaper than after fix-out, and most builders willingly fix at this stage.",
    ctaLabel: 'Find an inspector',
  },
  'First Fix': {
    category: 'professional:interior_designer',
    eyebrow: 'At your stage',
    headline: 'Lock in your finishes selections with an interior designer.',
    body:
      "Tapware, lighting, tiles, paint colours, splashbacks. Every finishes decision compounds. An interior designer at this stage saves you from $30k of changed-mind moments and pulls the whole scheme together.",
    ctaLabel: 'Find an interior designer',
  },
  'Insulation and Linings': {
    category: 'partner:house_inspect',
    eyebrow: 'Coming up: Practical Completion',
    headline: 'Pre-book your PCI inspection now.',
    body:
      "PCI bookings disappear fast as builders accelerate to handover. Lock in House Inspect Australia now so your independent walkthrough is locked in for the day handover lands.",
    ctaLabel: 'Find an inspector',
  },
  'Fixing': {
    category: 'partner:house_inspect',
    eyebrow: 'Coming up: Practical Completion',
    headline: 'Pre-book your PCI inspection now.',
    body:
      "Once fixing wraps, PCI bookings disappear fast. Lock in House Inspect Australia now so your independent walkthrough is ready the day handover lands.",
    ctaLabel: 'Find an inspector',
  },
  'Second Fix': {
    category: 'partner:house_inspect',
    eyebrow: 'At your stage',
    headline: 'Get your PCI booked before handover lands.',
    body:
      "House Inspect Australia's photo-rich PCI report gives you a defects list you can hand to your builder before signing handover. Every issue documented with location, photo and recommended fix.",
    ctaLabel: 'Find an inspector',
  },
  'Practical Completion': {
    category: 'partner:house_inspect',
    eyebrow: 'At your stage',
    headline: "Independent PCI is your last shield before keys.",
    body:
      "Don't accept handover without an independent PCI. House Inspect Australia's photo-rich report gives you the leverage to get every defect rectified BEFORE you sign. Once you've signed, a lot of builders go quiet.",
    ctaLabel: 'Find an inspector',
  },
  'Final/Completion': {
    category: 'partner:house_inspect',
    eyebrow: 'At your stage',
    headline: "Independent PCI is your last shield before keys.",
    body:
      "Don't accept handover without an independent PCI. House Inspect Australia's photo-rich report gives you the leverage to get every defect rectified BEFORE you sign. Once you've signed, a lot of builders go quiet.",
    ctaLabel: 'Find an inspector',
  },
  'Living in - First 30 Days': {
    category: 'partner:house_inspect',
    eyebrow: 'At your stage',
    headline: 'The first 90 days is your warranty defect window.',
    body:
      "Document every niggle while it's covered by builder warranty. A House Inspect Australia post-handover report catalogues defects in a format builders accept, saving you the back-and-forth.",
    ctaLabel: 'Find an inspector',
  },
  'Maintenance Period': {
    category: 'partner:house_inspect',
    eyebrow: 'At your stage',
    headline: "Last shot at builder-funded defect repairs.",
    body:
      "The 12-month maintenance period is when builders are still on the hook to fix things. An independent inspection now gives you the ammunition to claim every remaining issue.",
    ctaLabel: 'Find an inspector',
  },
};

// Best-effort match, returns null if there's no mapping for the
// current stage (in which case we suppress the strip entirely).
export function getStageRecommendation(stage) {
  if (!stage) return null;
  return STAGE_RECOMMENDATION_MAP[stage] || null;
}

// ─── A2: Next-stage prediction ─────────────────────────────────────
// The canonical iOS stage progression for a new-home build, in order.
// Used to predict the 2-3 NEXT professionals a user will need beyond
// their current stage. Stage labels match what iOS stores in
// profile.building_stage (and what the workspace dashboard renders).
const STAGE_ORDER = [
  'Thinking of building',
  'Signed up to a builder',
  'Construction Drawings/Master Plans',
  'Engineering',
  'Energy Rating',
  'Building Costs',
  'Selections',
  'Site Start',
  'Slab Pour',
  'Slab Down',
  'Frame Stage',
  'Lockup Stage',
  'Lockup',
  'First Fix',
  'Insulation and Linings',
  'Fixing',
  'Second Fix',
  'Practical Completion',
  'Final/Completion',
  'Living in - First 30 Days',
  'Maintenance Period',
];

/**
 * Returns up to `count` upcoming-stage recommendations beyond the
 * current stage, deduped by category so the user doesn't see the
 * same partner twice. Returns [] if the current stage isn't in the
 * canonical progression OR the user is past the end.
 */
export function getUpcomingStageRecommendations(currentStage, count = 3) {
  if (!currentStage) return [];
  const idx = STAGE_ORDER.indexOf(currentStage);
  if (idx === -1 || idx === STAGE_ORDER.length - 1) return [];
  const out = [];
  const seenCategories = new Set();
  for (let i = idx + 1; i < STAGE_ORDER.length && out.length < count; i++) {
    const stage = STAGE_ORDER[i];
    const rec = STAGE_RECOMMENDATION_MAP[stage];
    if (!rec) continue;
    if (seenCategories.has(rec.category)) continue;
    seenCategories.add(rec.category);
    out.push({ stage, ...rec });
  }
  return out;
}
